import {
  type HttpValidationError,
  LoginService,
  type Message,
  type UpdatePassword,
  type UserSortField,
  type UsersPublic,
  UsersService,
} from "@common/client"
import {
  type InfiniteData,
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"
import { AxiosError } from "axios"
import { useMemo } from "react"
import {
  makeAxiosMutation,
  makeNoArgsQuery,
  makeQuery,
  parseBlobErrors,
  unwrap,
} from "./utils"

export const usersBaseKey = ["users"] as const
export const usersInfiniteBaseKey = ["users-infinite"] as const

export const userKey = (user_id: string) => [...usersBaseKey, user_id] as const

export const userQuery = makeQuery({
  serviceCall: UsersService.readUserById,
  queryKey: ({ user_id }) => userKey(user_id),
})

export const currentUserKey = ["currentUser"] as const

export const currentUserQuery = makeNoArgsQuery({
  serviceCall: UsersService.readUserMe,
  queryKey: currentUserKey,
  staleTime: 60_000,
})

export const usersSearchKey = (
  params: Parameters<typeof UsersService.readUsers>[0],
) => [...usersBaseKey, "search", params] as const

export const usersSearchQuery = makeQuery({
  serviceCall: UsersService.readUsers,
  queryKey: usersSearchKey,
})

export const reportersKey = (
  params: Parameters<typeof UsersService.listReporters>[0],
) => [...usersBaseKey, "reporters", params] as const

export const reportersQuery = makeQuery({
  serviceCall: UsersService.listReporters,
  queryKey: reportersKey,
})

export const salesRepsKey = (
  params: Parameters<typeof UsersService.listSalesReps>[0],
) => [...usersBaseKey, "sales-reps", params] as const

export const salesRepsQuery = makeQuery({
  serviceCall: UsersService.listSalesReps,
  queryKey: salesRepsKey,
})

export const quoteReviewersKey = (
  params: Parameters<typeof UsersService.listQuoteReviewers>[0],
) => [...usersBaseKey, "quote-reviewers", params] as const

export const quoteReviewersQuery = makeQuery({
  serviceCall: UsersService.listQuoteReviewers,
  queryKey: quoteReviewersKey,
})

export const inspectorsKey = (
  params: Parameters<typeof UsersService.listInspectors>[0],
) => [...usersBaseKey, "inspectors", params] as const

export const inspectorsQuery = makeQuery({
  serviceCall: UsersService.listInspectors,
  queryKey: inspectorsKey,
})

export const useBulkCreateUsersMutation = makeAxiosMutation({
  serviceCall: UsersService.createMultipleUsers,
  invalidates: () => [usersBaseKey, usersInfiniteBaseKey],
})

export const useUpdateUserMutation = makeAxiosMutation({
  serviceCall: UsersService.updateUser,
  invalidates: () => [usersBaseKey, usersInfiniteBaseKey],
})

export const useResendLoginEmailMutation = makeAxiosMutation({
  serviceCall: UsersService.resendLoginEmail,
  invalidates: () => [usersBaseKey],
})

export function useDeleteUserMutation() {
  const queryClient = useQueryClient()

  return useMutation<
    Message,
    AxiosError<HttpValidationError>,
    { user_id: string }
  >({
    mutationFn: (data) => UsersService.deleteUser(data).then(unwrap),
    onSuccess: (_, { user_id }) =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: usersBaseKey,
          predicate: (query) => query.queryKey[1] !== user_id,
        }),
        queryClient.invalidateQueries({ queryKey: usersInfiniteBaseKey }),
      ]),
  })
}

export const useUpdateUserMeMutation = makeAxiosMutation({
  serviceCall: UsersService.updateUserMe,
  invalidates: () => [currentUserKey],
})

export const useUpdatePasswordMeMutation = makeAxiosMutation({
  serviceCall: async (input: { updatePassword: UpdatePassword }) => {
    const response = await UsersService.updatePasswordMe(input)
    if (!(response instanceof AxiosError)) {
      localStorage.setItem("access_token", response.data.access_token)
    }
    return response
  },
  invalidates: () => [currentUserKey],
})

export const useSetSigningPinMeMutation = makeAxiosMutation({
  serviceCall: UsersService.setSigningPinMe,
  invalidates: () => [currentUserKey],
})

export const useRemoveSigningPinMeMutation = makeAxiosMutation({
  serviceCall: UsersService.removeSigningPinMe,
  invalidates: () => [currentUserKey],
})

export const useRecoverPasswordMutation = makeAxiosMutation({
  serviceCall: LoginService.recoverPassword,
  invalidates: () => [],
})

export const useResetPasswordMutation = makeAxiosMutation({
  serviceCall: LoginService.resetPassword,
  invalidates: () => [],
})

export const useAdminResetPasswordMutation = makeAxiosMutation({
  serviceCall: LoginService.adminResetPassword,
  invalidates: () => [usersBaseKey],
})

export const useExportUsersMutation = makeAxiosMutation({
  serviceCall: parseBlobErrors(
    (params: Parameters<typeof UsersService.exportUsers>[0]) =>
      UsersService.exportUsers(params, { responseType: "blob" }),
  ),
  invalidates: () => [],
})

const USERS_PER_PAGE = 25

export type UsersListParams = {
  search?: string | null
  role_ids?: string[] | null
  is_active?: boolean | null
  sort_by?: UserSortField
  sort_direction?: "asc" | "desc"
}

export function useUsers(params: UsersListParams = {}) {
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    refetch,
  } = useInfiniteQuery<
    UsersPublic,
    Error,
    InfiniteData<UsersPublic>,
    readonly [...typeof usersInfiniteBaseKey, UsersListParams],
    number
  >({
    queryKey: [...usersInfiniteBaseKey, params],
    queryFn: ({ pageParam }) =>
      UsersService.readUsers({
        skip: pageParam,
        limit: USERS_PER_PAGE,
        ...params,
      }).then(unwrap),
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.length * USERS_PER_PAGE
      return loaded < (lastPage.count ?? 0) ? loaded : undefined
    },
    initialPageParam: 0,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  })

  const users = useMemo(() => data?.pages.flatMap((p) => p.data) ?? [], [data])
  const totalCount = data?.pages[0]?.count ?? 0

  return {
    users,
    totalCount,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    refetch,
  }
}
